import { animate, state, style, transition, trigger } from "@angular/animations";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import * as moment from "moment";
import { Period } from "../../models/period.model";
import { LanguageService } from "../../services/language.service";
import { PeriodService } from "../../services/period.service";
import { PeriodSumLine } from "./period-sum-line.model";

@Component({
  selector: "app-period-sum",
  templateUrl: "./period-sum.component.html",
  styleUrls: ["./period-sum.component.css"],
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0 })),
      transition("void <=> *", animate(400)),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodSumComponent {
  @Input() title: string;
  @Input() periodType = 1; // Week = 1, Month = 2, Year = 3
  @Input() fromDate: moment.Moment;
  @Input() toDate: moment.Moment;
  @Input() data: any;
  @Input() periodSumLines: PeriodSumLine[];
  @Input() showPeriodTitles = true;
  @Input() selectable = true;
  @Input() selected = false;

  constructor(public languageService: LanguageService, private periodService: PeriodService) {}

  get periodChunks(): Period[] {
    return this.periodService.getMomentPeriod(this.fromDate, this.toDate, this.periodType, true);
  }

  getPeriodSum(period: Period, periodSumLine: PeriodSumLine): string {
    if (this.data?.length === 0) { return null; }
    const recordsWithinPeriod = this.data?.filter((d: any) => new Date(d[periodSumLine.dateFieldName]) >= period.fromDate && new Date(d[periodSumLine.dateFieldName]) <= period.toDate);
    if (recordsWithinPeriod?.length === 0) { return null; }
    if (periodSumLine.isRate) { return this.getPeriodRate(recordsWithinPeriod, periodSumLine); }
    return this.getValueWithPrecision(recordsWithinPeriod?.map((d: any) => d[periodSumLine.fieldName]).reduce((a: number, b: number) => a + b));
  }

  getPeriodTotalSum(periodSumLine: PeriodSumLine): string {
    if (this.data?.length === 0) { return null; }
    if (periodSumLine.isRate) { return this.getPeriodTotalRate(periodSumLine); }
    return this.getValueWithPrecision(this.data?.map((d: any) => d[periodSumLine.fieldName]).reduce((a: number, b: number) => a + b));
  }

  private getPeriodRate(recordsWithinPeriod: any, periodSumLine: PeriodSumLine): string {
    const sumOrigin = recordsWithinPeriod?.map((d: any) => d[periodSumLine.rateOriginFieldName]).reduce((a: number, b: number) => a + b) ?? null;
    const sum = recordsWithinPeriod?.map((d: any) => d[periodSumLine.rateFieldName]).reduce((a: number, b: number) => a + b) ?? null;
    if (!sumOrigin || !sum) { return null; }
    return `${this.getValueWithPrecision(sumOrigin * 100 / sum, periodSumLine.precision)}%`;
  }

  private getPeriodTotalRate(periodSumLine: PeriodSumLine): string {
    const sumOrigin = this.data?.map((d: any) => d[periodSumLine.rateOriginFieldName]).reduce((a: number, b: number) => a + b) ?? null;
    const sum = this.data?.map((d: any) => d[periodSumLine.rateFieldName]).reduce((a: number, b: number) => a + b) ?? null;
    if (!sumOrigin || !sum) { return null; }
    return `${this.getValueWithPrecision(sumOrigin * 100 / sum, periodSumLine.precision)}%`;
  }

  private getValueWithPrecision(value?: number, precision?: number): string {
    if (!value) { return null; }
    return parseFloat(value.toFixed(precision ?? 2)).toString();
  }
}
